import type { User } from '../types/types.auth';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import LoginForm from './LoginForm';
import SignUpCard from './SignUpForm';

const ModalOverlay = styled.div`
  position: fixed;
  z-index: 2300;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
  padding: 16px;

  @media (max-height: 800px), (max-width: 768px) {
    align-items: flex-start;
    padding: 16px 8px;
  }
`;

interface SignUpOrLogInModalProps {
  open: boolean;
  onClose: () => void;
  contextMessage?: string;
  handleAuthSuccess?: (user: User) => void;
  initialMode?: 'signup' | 'login';
  disableClickOutside?: boolean;
  signUpFlag?: string;
}

const SignUpOrLogInModal: React.FC<SignUpOrLogInModalProps> = ({
  open,
  onClose,
  contextMessage,
  handleAuthSuccess,
  initialMode = 'signup',
  disableClickOutside = false,
  signUpFlag,
}) => {
  const [mode, setMode] = useState<'signup' | 'login'>(initialMode);

  useEffect(() => {
    // Only update mode when modal opens or initialMode actually changes
    if (open) {
      setMode(initialMode);
    }
  }, [open, initialMode]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [open, onClose]);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = () => {
      onClose();
    };

    if (open) {
      // Push a new state when modal opens
      window.history.pushState({ modalOpen: true }, '');
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [open, onClose]);

  // Clean up history when modal closes normally
  useEffect(() => {
    if (!open && window.history.state?.modalOpen) {
      window.history.back();
    }
  }, [open]);

  if (!open)
    return null;

  const handleSwitchToSignIn = () => setMode('login');
  const handleSwitchToSignUp = () => setMode('signup');

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (disableClickOutside)
      return;
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <ModalOverlay data-testid="auth-modal" onClick={handleOverlayClick}>
      <div onClick={e => e.stopPropagation()}>
        {mode === 'signup'
          ? (
              <SignUpCard
                contextMessage={contextMessage}
                handleSuccess={handleAuthSuccess}
                isModal={true}
                onSwitchToSignIn={handleSwitchToSignIn}
                signUpFlag={signUpFlag}
              />
            )
          : (
              <LoginForm
                contextMessage={contextMessage}
                handleSuccess={handleAuthSuccess}
                isModal={true}
                onSwitchToSignUp={handleSwitchToSignUp}
              />
            )}
      </div>
    </ModalOverlay>
  );
};

export default SignUpOrLogInModal;
